/**
 * AuditLogTable
 *
 * Displays a filterable table of audit log entries for team activity.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/authenticatedApi';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Filter,
  RefreshCw,
  Loader2,
  UserPlus,
  UserMinus,
  Settings,
  Mail,
  Shield,
  Trash2,
  Edit,
} from 'lucide-react';

interface AuditLogEntry {
  id: string;
  tenantId: string;
  userId: string;
  action: string;
  targetId: string | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  user?: {
    email: string;
    firstName: string | null;
  };
}

interface AuditLogTableProps {
  tenantId?: string;
}

// Action type labels and icons
const actionConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  tenant_created: { label: 'Team Created', icon: Settings, color: 'text-green-500' },
  tenant_updated: { label: 'Team Updated', icon: Edit, color: 'text-blue-500' },
  tenant_deleted: { label: 'Team Deleted', icon: Trash2, color: 'text-red-500' },
  member_added: { label: 'Member Added', icon: UserPlus, color: 'text-green-500' },
  member_removed: { label: 'Member Removed', icon: UserMinus, color: 'text-red-500' },
  member_role_changed: { label: 'Role Changed', icon: Shield, color: 'text-yellow-500' },
  invitation_created: { label: 'Invitation Sent', icon: Mail, color: 'text-blue-500' },
  invitation_accepted: { label: 'Invitation Accepted', icon: UserPlus, color: 'text-green-500' },
  invitation_cancelled: { label: 'Invitation Cancelled', icon: Trash2, color: 'text-gray-500' },
  invitation_resent: { label: 'Invitation Resent', icon: RefreshCw, color: 'text-blue-500' },
  ownership_transferred: { label: 'Ownership Transferred', icon: Shield, color: 'text-purple-500' },
  settings_updated: { label: 'Settings Updated', icon: Settings, color: 'text-blue-500' },
};

export const AuditLogTable: React.FC<AuditLogTableProps> = () => {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filterAction, setFilterAction] = useState<string>('');

  const limit = 20;

  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      if (filterAction) {
        params.append('action', filterAction);
      }

      const response = await api.get(`/tenants/current/audit-log?${params}`);
      setEntries(response.data.entries);
      setTotal(response.data.total);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [offset, filterAction]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  // Reset offset when filter changes
  useEffect(() => {
    setOffset(0);
  }, [filterAction]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionDescription = (entry: AuditLogEntry): string => {
    const actorName = entry.user?.firstName || entry.user?.email || 'Unknown user';
    const metadata = entry.metadata || {};

    switch (entry.action) {
      case 'tenant_created':
        return `${actorName} created the team`;
      case 'tenant_updated':
        return `${actorName} updated team settings`;
      case 'tenant_deleted':
        return `${actorName} deleted the team`;
      case 'member_added':
        return `${actorName} added ${metadata.memberEmail || 'a member'} as ${metadata.role || 'member'}`;
      case 'member_removed':
        return `${actorName} removed ${metadata.memberEmail || 'a member'}`;
      case 'member_role_changed':
        return `${actorName} changed ${metadata.memberEmail || 'member'}'s role from ${metadata.oldRole} to ${metadata.newRole}`;
      case 'invitation_created':
        return `${actorName} invited ${metadata.email || 'someone'} as ${metadata.role || 'member'}`;
      case 'invitation_accepted':
        return `${metadata.memberEmail || 'A user'} accepted the invitation`;
      case 'invitation_cancelled':
        return `${actorName} cancelled invitation for ${metadata.email || 'someone'}`;
      case 'invitation_resent':
        return `${actorName} resent invitation to ${metadata.email || 'someone'}`;
      case 'ownership_transferred':
        return `${actorName} transferred ownership to ${metadata.newOwnerEmail || 'another member'}`;
      case 'settings_updated':
        return `${actorName} updated ${metadata.setting || 'settings'}`;
      default:
        return `${actorName} performed an action`;
    }
  };

  const getActionConfig = (action: string) => {
    return actionConfig[action] || { label: action, icon: Settings, color: 'text-gray-500' };
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-4">
      {/* Header with Filter */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Activity Log
        </h3>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm rounded-lg border appearance-none"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                borderColor: 'var(--color-border-default)',
                color: 'var(--color-text-primary)',
              }}
            >
              <option value="">All Actions</option>
              {Object.entries(actionConfig).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchAuditLog}
            disabled={loading}
            className="p-2 rounded-lg border hover:bg-tertiary transition-colors"
            style={{ borderColor: 'var(--color-border-default)' }}
          >
            <RefreshCw
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              style={{ color: 'var(--color-text-secondary)' }}
            />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderColor: 'rgba(239, 68, 68, 0.3)',
          }}
        >
          <p className="text-sm" style={{ color: '#ef4444' }}>
            {error}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && entries.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-accent-teal)' }} />
        </div>
      )}

      {/* Empty State */}
      {!loading && entries.length === 0 && (
        <div
          className="text-center py-12 rounded-lg border"
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            borderColor: 'var(--color-border-default)',
          }}
        >
          <Clock
            className="w-12 h-12 mx-auto mb-3"
            style={{ color: 'var(--color-text-tertiary)' }}
          />
          <p style={{ color: 'var(--color-text-secondary)' }}>
            {filterAction ? 'No matching activities found' : 'No activity yet'}
          </p>
        </div>
      )}

      {/* Activity List */}
      {entries.length > 0 && (
        <div
          className="rounded-lg border overflow-hidden"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-border-default)',
          }}
        >
          <div className="divide-y" style={{ borderColor: 'var(--color-border-default)' }}>
            {entries.map((entry) => {
              const config = getActionConfig(entry.action);
              const Icon = config.icon;

              return (
                <div
                  key={entry.id}
                  className="px-4 py-3 flex items-start gap-3 hover:bg-tertiary transition-colors"
                >
                  {/* Icon */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5`}
                    style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                  >
                    <Icon className={`w-4 h-4 ${config.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {getActionDescription(entry)}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                        {formatDate(entry.createdAt)}
                      </span>
                      {entry.ipAddress && (
                        <>
                          <span style={{ color: 'var(--color-text-tertiary)' }}>·</span>
                          <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                            {entry.ipAddress}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Action Badge */}
                  <span
                    className="text-xs px-2 py-1 rounded-full shrink-0"
                    style={{
                      backgroundColor: 'var(--color-bg-tertiary)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {config.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ borderTop: '1px solid var(--color-border-default)' }}
            >
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="p-2 rounded-lg border hover:bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ borderColor: 'var(--color-border-default)' }}
                >
                  <ChevronLeft
                    className="w-4 h-4"
                    style={{ color: 'var(--color-text-secondary)' }}
                  />
                </button>
                <span className="text-sm px-3" style={{ color: 'var(--color-text-secondary)' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setOffset(Math.min((totalPages - 1) * limit, offset + limit))}
                  disabled={offset + limit >= total}
                  className="p-2 rounded-lg border hover:bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ borderColor: 'var(--color-border-default)' }}
                >
                  <ChevronRight
                    className="w-4 h-4"
                    style={{ color: 'var(--color-text-secondary)' }}
                  />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditLogTable;
